import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import PasswordInput from "../components/PasswordInput";
import Turnstile from "../components/Turnstile";
import { AuthShell, AuthLogo, FormError, GoogleBlock, RotatingWords, authQuery, parseErr } from "../components/AuthBits";

export default function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const next = sp.get("next") ?? "/";
  const invite = sp.get("invite") ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaNeeded, setMfaNeeded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  // SSO: look up the entered email's domain; if a workspace has SSO, hand
  // off to the IdP via a full-page redirect.
  const sso = useMutation({
    mutationFn: () => api.sso.lookup(email.trim().toLowerCase()),
    onSuccess: (r) => {
      if (r.found) {
        window.location.href = `/api/sso/start?email=${encodeURIComponent(email.trim().toLowerCase())}&next=${encodeURIComponent(next)}`;
      } else {
        setErr("No SSO is configured for that email's domain. Use your password or Google.");
      }
    },
    onError: () => setErr("Couldn't start SSO. Try again."),
  });

  const login = useMutation({
    mutationFn: () => api.login({ email: email.trim().toLowerCase(), password, invite: invite || undefined, mfaCode: mfaNeeded ? mfaCode.trim() : undefined, turnstileToken: turnstileToken || undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      if (invite) await qc.invalidateQueries({ queryKey: ["workspaces"] });
      nav(next);
    },
    onError: (e) => {
      const m = String((e as Error).message ?? "");
      if (m.includes("mfa_required")) {
        setMfaNeeded(true);
        setErr(null);
      } else if (m.includes("bad_mfa_code")) {
        setErr("That code didn't match. Try the next one your authenticator shows.");
      } else {
        setErr(parseErr(e, "Couldn't sign in. Check email and password."));
      }
    },
  });

  // Legacy deep links: /login?mode=signup and ?mode=forgot predate the
  // dedicated pages. Forward them so old marketing links keep working.
  const legacyMode = sp.get("mode");
  if (legacyMode === "signup") return <Navigate to={`/signup${authQuery(sp)}`} replace />;
  if (legacyMode === "forgot") return <Navigate to={`/forgot${authQuery(sp)}`} replace />;

  return (
    <AuthShell>
      <div className="text-center space-y-3">
        <AuthLogo />
        {/* Hard line break: with a single wrapping line, the width
            animation crosses the wrap threshold mid-transition and a
            word snaps to the next line — a discrete jump no easing can
            hide. Fixed two-line structure = only smooth motion. */}
        <h1 className="text-2xl font-bold tracking-tight">
          The <RotatingWords words={["API client", "flow runner", "webhook inbox"]} />
          <br />your team can share.
        </h1>
        <p className="text-sm muted">Welcome back — sign in to your workspace.</p>
      </div>

      <GoogleBlock sp={sp} />

      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); setErr(null); login.mutate(); }}
      >
        <input
          className="input"
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <PasswordInput
          required
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {mfaNeeded && (
          <input
            className="input input-mono"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="6-digit code (or recovery code)"
            autoComplete="one-time-code"
            inputMode="text"
            autoFocus
          />
        )}
        <Turnstile onToken={setTurnstileToken} />
        <FormError msg={err} />
        <LoadingButton
          type="submit"
          className="btn-primary w-full justify-center"
          pending={login.isPending}
          pendingLabel="Signing in…"
        >
          Sign in
        </LoadingButton>
      </form>

      <button
        type="button"
        className="btn w-full justify-center"
        disabled={sso.isPending}
        onClick={() => {
          setErr(null);
          if (!email.trim()) { setErr("Enter your work email, then use SSO."); return; }
          sso.mutate();
        }}
      >
        {sso.isPending ? "Redirecting…" : "Sign in with SSO"}
      </button>

      <div className="flex items-center justify-between text-xs muted">
        <Link to={`/forgot${authQuery(sp)}`} className="hover:underline">Forgot password?</Link>
        <Link to={`/signup${authQuery(sp)}`} className="hover:underline">
          Need an account? <b className="font-semibold text-text">Sign up</b>
        </Link>
      </div>
    </AuthShell>
  );
}
